/**
 * Zamówienia od handlowców — kolejka Biura zamówień (NX-419), klucze `sord.*`. Scalane w słownik przez `extra.ts`;
 * `sordEn` jest typowane względem `sordPl`, więc brak klucza = błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const sordPl = {
  nav: 'Zamówienia od handlowców',
  title: 'Zamówienia od handlowców',
  description: 'Kolejka Biura zamówień — wprowadź do Subiektu; NEXOPS sam dopasuje ZK po imporcie.',
  kpi: {
    toEnter: 'Do wprowadzenia',
    entered: 'Czeka na ZK',
    accepted: 'Przyjęte',
    rejected: 'Odrzucone',
  },
  filter: {
    status: 'Status',
    allStatuses: 'Wszystkie statusy',
    delivery: 'Dostawa',
    allDays: 'Wszystkie dni',
    author: 'Handlowiec',
    allAuthors: 'Wszyscy',
  },
  status: {
    SUBMITTED: 'Do wprowadzenia',
    ENTERED: 'Wprowadzone, czeka na ZK',
    ACCEPTED: 'Przyjęte',
    REJECTED: 'Odrzucone',
    CANCELLED: 'Wycofane',
  },
  acceptedWithChanges: 'Przyjęte ze zmianą',
  col: {
    sent: 'Wysłane',
    customer: 'Klient',
    author: 'Handlowiec',
    delivery: 'Dostawa',
    lines: 'Poz.',
    net: 'Netto',
    notes: 'Uwagi',
    status: 'Status',
    symbol: 'Symbol',
    product: 'Towar',
    qty: 'Ilość',
    price: 'Cena',
    erpQty: 'W ZK',
  },
  priceRequestBadge: 'prośba o cenę',
  today: 'dziś {time}',
  yesterday: 'wczoraj {time}',
  empty: 'Brak zamówień przy wybranych filtrach.',
  limited: 'Pokazano {count} najnowszych zamówień — zawęź filtry.',
  error: 'Nie udało się wczytać zamówień',
  retry: 'Spróbuj ponownie',
  pickOrder: 'Wybierz zamówienie z listy, żeby zobaczyć pozycje.',
  detail: {
    erpId: 'Nr kontrahenta w Subiekcie: {id}',
    delivery: 'dostawa {date}',
    route: 'trasa {code}',
    author: 'Handlowiec: {name}',
    noPrice: 'brak ceny',
    priceRequest: '{product}: prośba o cenę {price} — „{note}”. Cenę ustala się w Subiekcie.',
    notes: 'Uwagi: {notes}',
    totalNet: 'Razem netto: {amount}',
    noVat: 'VAT i brutto — po imporcie stawek VAT towarów.',
    withoutPrice: 'Pozycje bez ceny w cenniku: {count} (poza sumą).',
    confirmation: {
      SENT: 'Potwierdzenie wysłane klientowi: {to}',
      FAILED: 'Potwierdzenia do klienta nie udało się wysłać.',
      NO_RECIPIENT: 'Klient nie ma adresu e-mail — potwierdzenie nie poszło.',
      NOT_REQUESTED: '',
    },
    entered: 'Wprowadzone przez {name}, {at}',
    rejected: 'Odrzucone przez {name}: „{reason}”',
    cancelled: 'Handlowiec wycofał zamówienie {at}.',
    accepted: 'W Subiekcie jako {number}',
    differences: 'W Subiekcie inaczej — kolumna „W ZK”.',
    diff: { NONE: 'bez zmian', CHANGED: 'inna ilość', REMOVED: 'brak w ZK', ADDED: 'dopisane w Subiekcie' },
    footnote: 'Po następnym imporcie z Subiektu NEXOPS znajdzie ZK tego klienta na ten dzień dostawy, zamknie zamówienie z numerem i pokaże handlowcowi różnice w pozycjach.',
  },
  action: {
    copy: 'Kopiuj pozycje do Subiektu',
    copied: 'Skopiowano {count} pozycji (symbol, TAB, ilość)',
    copyFailed: 'Nie udało się skopiować do schowka',
    pdf: 'PDF',
    pdfFailed: 'Nie udało się przygotować PDF',
    entered: 'Wprowadzone',
    enteredDone: 'Oznaczone jako wprowadzone do Subiektu',
    reject: 'Odrzuć z powodem',
    failed: 'Nie udało się zmienić zamówienia',
    changed: 'Zamówienie zmieniło się w międzyczasie — odświeżono.',
  },
  rejectDialog: {
    title: 'Odrzuć zamówienie',
    description: 'Handlowiec dostanie powiadomienie z tym powodem.',
    reason: 'Powód',
    placeholder: 'np. Brak masła 82% do 05.11 — zadzwoń do klienta, zaproponuj 80%.',
    cancel: 'Anuluj',
    submit: 'Odrzuć',
    done: 'Zamówienie odrzucone',
  },
};

export type SordMessages = typeof sordPl;

export const sordEn: Shape<SordMessages> = {
  nav: 'Orders from sales reps',
  title: 'Orders from sales reps',
  description: 'Order desk queue — enter into Subiekt; NEXOPS matches the customer order (ZK) after the import.',
  kpi: {
    toEnter: 'To enter',
    entered: 'Waiting for ZK',
    accepted: 'Accepted',
    rejected: 'Rejected',
  },
  filter: {
    status: 'Status',
    allStatuses: 'All statuses',
    delivery: 'Delivery',
    allDays: 'All days',
    author: 'Sales rep',
    allAuthors: 'Everyone',
  },
  status: {
    SUBMITTED: 'To enter',
    ENTERED: 'Entered, waiting for ZK',
    ACCEPTED: 'Accepted',
    REJECTED: 'Rejected',
    CANCELLED: 'Withdrawn',
  },
  acceptedWithChanges: 'Accepted with changes',
  col: {
    sent: 'Sent',
    customer: 'Customer',
    author: 'Sales rep',
    delivery: 'Delivery',
    lines: 'Lines',
    net: 'Net',
    notes: 'Notes',
    status: 'Status',
    symbol: 'Code',
    product: 'Product',
    qty: 'Qty',
    price: 'Price',
    erpQty: 'In ZK',
  },
  priceRequestBadge: 'price request',
  today: 'today {time}',
  yesterday: 'yesterday {time}',
  empty: 'No orders match the filters.',
  limited: 'Showing the {count} newest orders — narrow the filters.',
  error: 'Could not load the orders',
  retry: 'Try again',
  pickOrder: 'Pick an order from the list to see its lines.',
  detail: {
    erpId: 'Customer no. in Subiekt: {id}',
    delivery: 'delivery {date}',
    route: 'route {code}',
    author: 'Sales rep: {name}',
    noPrice: 'no price',
    priceRequest: '{product}: price request {price} — “{note}”. The price is set in Subiekt.',
    notes: 'Notes: {notes}',
    totalNet: 'Net total: {amount}',
    noVat: 'VAT and gross — once product VAT rates are imported.',
    withoutPrice: 'Lines without a price-list price: {count} (not in the total).',
    confirmation: {
      SENT: 'Confirmation sent to the customer: {to}',
      FAILED: 'The confirmation to the customer could not be sent.',
      NO_RECIPIENT: 'The customer has no e-mail address — no confirmation was sent.',
      NOT_REQUESTED: '',
    },
    entered: 'Entered by {name}, {at}',
    rejected: 'Rejected by {name}: “{reason}”',
    cancelled: 'The sales rep withdrew the order {at}.',
    accepted: 'In Subiekt as {number}',
    differences: 'Different in Subiekt — see the “In ZK” column.',
    diff: { NONE: 'unchanged', CHANGED: 'other quantity', REMOVED: 'not in ZK', ADDED: 'added in Subiekt' },
    footnote: 'After the next import from Subiekt NEXOPS finds this customer’s ZK for this delivery day, closes the order with the number and shows the sales rep the differences.',
  },
  action: {
    copy: 'Copy lines for Subiekt',
    copied: 'Copied {count} lines (code, TAB, quantity)',
    copyFailed: 'Could not copy to the clipboard',
    pdf: 'PDF',
    pdfFailed: 'Could not prepare the PDF',
    entered: 'Entered',
    enteredDone: 'Marked as entered into Subiekt',
    reject: 'Reject with a reason',
    failed: 'Could not change the order',
    changed: 'The order changed in the meantime — refreshed.',
  },
  rejectDialog: {
    title: 'Reject the order',
    description: 'The sales rep gets a notification with this reason.',
    reason: 'Reason',
    placeholder: 'e.g. No 82% butter until 05.11 — call the customer, offer 80%.',
    cancel: 'Cancel',
    submit: 'Reject',
    done: 'Order rejected',
  },
};
